import { useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import { withTheme, withStyles } from "@material-ui/core/styles";
import {
  Paper, Typography, Grid, TextField, Button, MenuItem, Box,
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
  Chip, Select, InputLabel,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import SaveIcon from "@material-ui/icons/Save";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { IconButton, Tooltip } from "@material-ui/core";
import { Autocomplete, formatMessage, withModulesManager, Helmet, baseApiUrl, apiHeaders } from "@stssocialst-stp/fe-core";
import { PRL_ROUTE_SESSION_PLANNING, PRL_ROUTE_SESSION_PLANNING_FORM } from "../constants";

const styles = (theme) => ({
  page: theme.page,
  paper: { ...theme.paper.paper, margin: theme.spacing(2), padding: theme.spacing(2) },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
    gap: theme.spacing(1),
    padding: theme.spacing(0, 1, 2, 1),
  },
  headerTitle: {
    marginLeft: theme.spacing(1),
    fontWeight: 500,
  },
  formControl: {
    marginTop: theme.spacing(1),
  },
  sessionHeader: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(0.5),
    display: "inline-block",
    marginBottom: theme.spacing(2),
  }
});

const DAYS_OF_WEEK = [
  { value: "SEGUNDA", label: "Segunda-feira" },
  { value: "TERCA", label: "Terça-feira" },
  { value: "QUARTA", label: "Quarta-feira" },
  { value: "QUINTA", label: "Quinta-feira" },
  { value: "SEXTA", label: "Sexta-feira" },
  { value: "SABADO", label: "Sábado" },
  { value: "DOMINGO", label: "Domingo" },
];

const MONTHS = [
  { value: "Janeiro", label: "Janeiro" },
  { value: "Fevereiro", label: "Fevereiro" },
  { value: "Março", label: "Março" },
  { value: "Abril", label: "Abril" },
  { value: "Maio", label: "Maio" },
  { value: "Junho", label: "Junho" },
  { value: "Julho", label: "Julho" },
  { value: "Agosto", label: "Agosto" },
  { value: "Setembro", label: "Setembro" },
  { value: "Outubro", label: "Outubro" },
  { value: "Novembro", label: "Novembro" },
  { value: "Dezembro", label: "Dezembro" },
];

function SessionPlanningEditPage(props) {
  const { classes, intl, history, location } = props;
  const readOnly = location?.state?.readOnly || false;
  const initialData = location?.state?.data || null;

  const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  const fetchQuery = `query GetSessaoPep($id: ID!) {
    sessaoPep(id: $id) {
      id
      codigoSessao
      dataPlanejamento
      modulo {
        id
        codigo
        nome
        ordem
      }
      coordenadorDistrital {
        id
        username
      }
      tecnicoSocial {
        id
        username
      }
      tecnicosFormadores {
        id
        username
        lastName
      }
      distrito {
        id
        code
        name
      }
      mesModuloAnterior
      diaSemana
      dataSessao
      horaSessao
      zona
      numeroFamilias
      grupoFamilia {
        id
        codigo
        nome
      }
      tempoDeslocamento
      feedbackDocumentacao
      temSupervisao
      observacoes
      status
    }
  }`;

  const districtQuery = `query GetDistritos($first: Int) {
    locations(first: $first, type: "D") {
      edges {
        node {
          id
          code
          name
        }
      }
    }
  }`;

  const coordinatorQuery = `query GetCoordinators {
    users(first: 100) {
      edges {
        node {
          id
          username
          lastName
        }
      }
    }
  }`;

  const socialTechnicianQuery = `query GetSocialTechnicians {
    users(first: 100) {
      edges {
        node {
          id
          username
          lastName
        }
      }
    }
  }`;

  const modulosPEPQuery = `query GetModulosPEP {
    modulosPep(ativo: true, orderBy: ["ordem"]) {
      edges {
        node {
          id
          codigo
          nome
          ordem
          duracao
        }
      }
    }
  }`;

  const familyGroupsQuery = `query GetGruposFamiliares($first: Int, $distritoId: String) {
    gruposFamiliares(first: $first, distritoId: $distritoId, ativo: true) {
      edges {
        node {
          id
          codigo
          nome
          numeroFamilias
        }
      }
    }
  }`;

  const createMultipleMutation = `mutation CreateMultipleSessoesPEP($input: CreateMultipleSessoesPEPMutationInput!) {
    createMultipleSessoesPep(input: $input) {
      clientMutationId
      internalId
    }
  }`;

  const updateMultipleMutation = `mutation UpdateMultipleSessoesPEP($input: UpdateMultipleSessoesPEPMutationInput!) {
    updateMultipleSessoesPep(input: $input) {
      clientMutationId
      internalId
    }
  }`;

  const [formData, setFormData] = useState({
    codigoSessao: "",
    dataPlanejamento: new Date().toISOString().slice(0, 10),
    coordenadorDistrital: null,
    tecnicosFormadores: [],
    distrito: null,
    modulo: null,
    mesModuloAnterior: "",
    observacoes: "",
    status: "PLAN",
  });

  const [sessionData, setSessionData] = useState({
    diaSemana: "",
    dataSessao: "",
    zona: "",
    numeroFamilias: 0,
    grupoFamilia: null,
    horaSessao: "",
    tempoDeslocamento: 0,
    feedbackDocumentacao: "",
    temSupervisao: false,
  });

  const [sessions, setSessions] = useState([]);
  const [codigoSessaoError, setCodigoSessaoError] = useState("");

  // Formats input into the pattern: AA-MOxx-MMM-YYYY
  const formatCodigoSessao = (value) => {
    const raw = value.replace(/-/g, "").toUpperCase();
    let p1 = [], p2 = [], p3 = [], p4 = [];
    let seg = 0;
    for (let i = 0; i < raw.length; i++) {
      const c = raw[i];
      if (seg === 0) {
        if (/[A-Z]/.test(c)) { p1.push(c); if (p1.length === 2) seg = 1; }
      } else if (seg === 1) {
        if (p2.length < 2 && /[A-Z]/.test(c)) { p2.push(c); }
        else if (p2.length >= 2 && /[0-9]/.test(c)) { p2.push(c); if (p2.length === 4) seg = 2; }
      } else if (seg === 2) {
        if (/[A-Z]/.test(c)) { p3.push(c); if (p3.length === 3) seg = 3; }
      } else if (seg === 3) {
        if (/[0-9]/.test(c)) { p4.push(c); if (p4.length === 4) break; }
      }
    }
    let result = p1.join("");
    if (p2.length > 0) result += "-" + p2.join("");
    if (p3.length > 0) result += "-" + p3.join("");
    if (p4.length > 0) result += "-" + p4.join("");
    return result;
  };

  const handleCodigoSessaoChange = (event) => {
    if (readOnly) return;
    const formatted = formatCodigoSessao(event.target.value);
    const parts = formatted.split("-");
    if (parts.length === 4 && parts[3].length === 4) {
      const year = parseInt(parts[3]);
      setCodigoSessaoError(year < 2000 ? "O ano deve ser igual ou superior a 2000." : "");
    } else {
      setCodigoSessaoError("");
    }
    setFormData((prev) => ({ ...prev, codigoSessao: formatted }));
  };

  const [districts, setDistricts] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [socialTechnicians, setSocialTechnicians] = useState([]);
  const [modules, setModules] = useState([]);
  // familyGroups stores { value, label, numeroFamilias } objects
  const [familyGroups, setFamilyGroups] = useState([]);

  useEffect(() => {
    if (initialData?.id) {
      fetchSession(initialData.id);
    }
    // Load dynamic data on mount
    fetchDistricts();
    fetchCoordinators();
    fetchSocialTechnicians();
    fetchModules();
    fetchFamilyGroups(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch family groups filtered by the selected district
  useEffect(() => {
    const distritoId = formData.distrito?.id || null;
    fetchFamilyGroups(distritoId);
  }, [formData.distrito?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSession = async (id) => {
    const response = await fetch(`${baseApiUrl}/graphql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCookie('csrftoken'),
        ...apiHeaders(),
      },
      body: JSON.stringify({ query: fetchQuery, variables: { id } }),
    });

    const result = await response.json();
    if (result.data?.sessaoPep) {
      const pep = result.data.sessaoPep;
      setFormData({
        ...pep,
        modulo: pep.modulo || null,
        tecnicosFormadores: (pep.tecnicosFormadores && pep.tecnicosFormadores.length > 0) 
          ? pep.tecnicosFormadores 
          : (pep.tecnicoSocial ? [pep.tecnicoSocial] : []),
      });
      // Populate sessions array for display
      setSessions([{
        id: pep.id,
        diaSemana: pep.diaSemana,
        dataSessao: pep.dataSessao,
        zona: pep.zona,
        numeroFamilias: pep.numeroFamilias,
        grupoFamilia: pep.grupoFamilia,
        horaSessao: pep.horaSessao,
        tempoDeslocamento: pep.tempoDeslocamento,
        feedbackDocumentacao: pep.feedbackDocumentacao,
        temSupervisao: pep.temSupervisao,
      }]);
    } else if (result.errors) {
      console.error('Error fetching session:', result.errors);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await fetch(`${baseApiUrl}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          ...apiHeaders(),
        },
        body: JSON.stringify({ query: districtQuery, variables: { first: 100 } }),
      });

      const result = await response.json();
      if (result.data?.locations?.edges) {
        const districtList = result.data.locations.edges.map(edge => ({
          value: edge.node.id,
          label: edge.node.name,
        }));
        setDistricts(districtList);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchCoordinators = async () => {
    try {
      const response = await fetch(`${baseApiUrl}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          ...apiHeaders(),
        },
        body: JSON.stringify({ query: coordinatorQuery }),
      });

      const result = await response.json();
      if (result.data?.users?.edges) {
        const coordinatorList = result.data.users.edges.map(edge => ({
          value: edge.node.id,
          label: edge.node.lastName,
        }));
        setCoordinators(coordinatorList);
      }
    } catch (error) {
      console.error('Error fetching coordinators:', error);
    }
  };

  const fetchSocialTechnicians = async () => {
    try {
      const response = await fetch(`${baseApiUrl}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          ...apiHeaders(),
        },
        body: JSON.stringify({ query: socialTechnicianQuery }),
      });

      const result = await response.json();
      if (result.data?.users?.edges) {
        const technicianList = result.data.users.edges.map(edge => ({
          value: edge.node.id,
          label: edge.node.lastName,
        }));
        setSocialTechnicians(technicianList);
      }
    } catch (error) {
      console.error('Error fetching social technicians:', error);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await fetch(`${baseApiUrl}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          ...apiHeaders(),
        },
        body: JSON.stringify({ query: modulosPEPQuery }),
      });

      const result = await response.json();
      if (result.data?.modulosPep?.edges) {
        const moduleList = result.data.modulosPep.edges.map(edge => ({
          value: edge.node.id,
          label: `${edge.node.codigo} - ${edge.node.nome}`,
        }));
        setModules(moduleList);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const fetchFamilyGroups = async (distritoId) => {
    try {
      const response = await fetch(`${baseApiUrl}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          ...apiHeaders(),
        },
        body: JSON.stringify({ query: familyGroupsQuery, variables: { first: 100, distritoId: distritoId || null } }),
      });

      const result = await response.json();
      if (result.data?.gruposFamiliares?.edges) {
        const groupList = result.data.gruposFamiliares.edges.map(edge => ({
          value: edge.node.id,
          label: `${edge.node.codigo} - ${edge.node.nome}`,
          numeroFamilias: edge.node.numeroFamilias || 0,
        }));
        setFamilyGroups(groupList);
      }
    } catch (error) {
      console.error('Error fetching family groups:', error);
    }
  };

  const handleChange = (field) => (event) => {
    if (readOnly) return;
    const { value } = event.target;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };


  // Busca todas as coordenações distritais ao montar
  const [districtCoordinations, setDistrictCoordinations] = useState([]);
  useEffect(() => {
    const fetchCoordenacoesDistritais = async () => {
      const query = `query ListCoordenacoes($first: Int) {
        coordenacoesDistritais(first: $first) {
          edges {
            node {
              id
              distrito { id name }
              coordenador { id lastName }
              tecnicosOperacionais { tecnico { id lastName } }
            }
          }
        }
      }`;
      try {
        const res = await fetch(`${baseApiUrl}/graphql`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRFToken": getCookie("csrftoken"), ...apiHeaders() },
          body: JSON.stringify({ query, variables: { first: 100 } }),
        });
        const json = await res.json();
        const edges = json?.data?.coordenacoesDistritais?.edges ?? [];
        setDistrictCoordinations(edges.map(e => e.node));
      } catch (e) { console.error(e); }
    };
    fetchCoordenacoesDistritais();
  }, []);

  // Ao selecionar distrito, preenche coordenador e técnicos sociais
  const handleSelectChange = (field) => async (event) => {
    if (readOnly) return;
    const { value } = event.target;
    setFormData((prev) => ({ ...prev, [field]: { id: value } }));
    if (field === 'distrito') {
      // Busca a coordenação do distrito selecionado
      const coord = districtCoordinations.find(c => c.distrito?.id === value);
      if (coord) {
        // Preenche coordenador
        if (coord.coordenador) {
          setFormData(prev => ({ ...prev, coordenadorDistrital: { id: coord.coordenador.id } }));
        }
        // Preenche técnicos sociais
        if (Array.isArray(coord.tecnicosOperacionais)) {
          setSocialTechnicians(coord.tecnicosOperacionais.map(t => ({ value: t.tecnico.id, label: t.tecnico.lastName })));
        } else {
          setSocialTechnicians([]);
        }
      } else {
        setFormData(prev => ({ ...prev, coordenadorDistrital: null, tecnicosFormadores: [] }));
        setSocialTechnicians([]);
      }
    }
  };


  // (Apenas a versão que consome districtCoordinations deve existir)

  const handleSessionChange = (field) => (event) => {
    if (readOnly) return;
    const { value } = event.target;
    setSessionData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSessionSelectChange = (field) => (event) => {
    if (readOnly) return;
    const { value } = event.target;
    if (field === 'grupoFamilia') {
      const selectedGroup = familyGroups.find(g => g.value === value);
      setSessionData((prev) => ({
        ...prev,
        grupoFamilia: { id: value },
        numeroFamilias: selectedGroup?.numeroFamilias ?? prev.numeroFamilias,
      }));
    } else {
      setSessionData((prev) => ({ ...prev, [field]: { id: value } }));
    }
  };

  const handleAddSession = () => {
    if (readOnly) return;
    setSessions((prev) => [...prev, { ...sessionData, id: Date.now() }]);
    setSessionData({
      diaSemana: "",
      dataSessao: "",
      zona: "",
      numeroFamilias: 0,
      grupoFamilia: null,
      horaSessao: "",
      tempoDeslocamento: 0,
      feedbackDocumentacao: "",
      temSupervisao: false,
    });
  };

  const handleRemoveSession = (id) => {
    if (readOnly) return;
    setSessions((prev) => prev.filter((session) => session.id !== id));
  };

  const handleSessionUpdate = (index, field, value) => {
    setSessions(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const handleSessionSelectUpdate = (index, field, value) => {
    if (field === 'grupoFamilia') {
      // Auto-fill numeroFamilias from the selected group
      const selectedGroup = familyGroups.find(g => g.value === value);
      setSessions(prev => prev.map((s, i) => i === index
        ? {
          ...s,
          grupoFamilia: { id: value },
          numeroFamilias: selectedGroup?.numeroFamilias ?? s.numeroFamilias,
        }
        : s
      ));
    } else {
      setSessions(prev => prev.map((s, i) => i === index ? { ...s, [field]: { id: value } } : s));
    }
  };

  const renderSessionForm = (session, index) => (
    <Box key={session.id} style={{ marginBottom: "24px", padding: "16px", border: "1px solid #e0e0e0", borderRadius: "4px" }}>
      <Typography variant="subtitle2" style={{ marginBottom: "16px", fontWeight: "bold" }}>
        {formatMessage(intl, "prl", "sessionPlanning.session")} {index + 1}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            label={formatMessage(intl, "prl", "sessionPlanning.dayOfWeek")}
            value={session.diaSemana}
            onChange={(event) => handleSessionUpdate(index, 'diaSemana', event.target.value)}
            variant="outlined"
            size="small"
            required
            disabled={readOnly}
          >
            {DAYS_OF_WEEK.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="date"
            label={formatMessage(intl, "prl", "sessionPlanning.plannedDate")}
            value={session.dataSessao || new Date().toISOString().slice(0, 10)}
            onChange={(event) => handleSessionUpdate(index, 'dataSessao', event.target.value)}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            required
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={formatMessage(intl, "prl", "sessionPlanning.zone")}
            placeholder="Ex: A, B, C, D"
            value={session.zona}
            onChange={(event) => handleSessionUpdate(index, 'zona', event.target.value)}
            variant="outlined"
            size="small"
            required
            InputLabelProps={{ shrink: true }}
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            label={formatMessage(intl, "prl", "sessionPlanning.selectFamilyGroup")}
            value={session.grupoFamilia?.id || ""}
            onChange={(event) => handleSessionSelectUpdate(index, 'grupoFamilia', event.target.value)}
            variant="outlined"
            size="small"
            required
            disabled={readOnly}
          >
            {familyGroups.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label={formatMessage(intl, "prl", "sessionPlanning.numFamilies")}
            value={session.numeroFamilias}
            onChange={(event) => handleSessionUpdate(index, 'numeroFamilias', parseInt(event.target.value) || 0)}
            variant="outlined"
            size="small"
            required
            disabled={true}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label={formatMessage(intl, "prl", "sessionPlanning.travelTime")}
            value={session.tempoDeslocamento}
            onChange={(event) => handleSessionUpdate(index, 'tempoDeslocamento', parseInt(event.target.value) || 0)}
            variant="outlined"
            size="small"
            required
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="time"
            label={formatMessage(intl, "prl", "sessionPlanning.sessionTime")}
            value={session.horaSessao}
            onChange={(event) => handleSessionUpdate(index, 'horaSessao', event.target.value)}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            required
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl component="fieldset" className={classes.formControl} disabled={readOnly}>
            <FormLabel component="legend">{formatMessage(intl, "prl", "sessionPlanning.isSupervised")}</FormLabel>
            <RadioGroup
              row
              value={session.temSupervisao ? "Sim" : "Não"}
              onChange={(event) => handleSessionUpdate(index, 'temSupervisao', event.target.value === "Sim")}
            >
              <FormControlLabel value="Sim" control={<Radio color="primary" />} label="Sim" />
              <FormControlLabel value="Não" control={<Radio color="primary" />} label="Não" />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={formatMessage(intl, "prl", "sessionPlanning.feedback")}
            placeholder="Feedback da documentação..."
            value={session.feedbackDocumentacao}
            onChange={(event) => handleSessionUpdate(index, 'feedbackDocumentacao', event.target.value)}
            variant="outlined"
            size="small"
            required
            InputLabelProps={{ shrink: true }}
            disabled={readOnly}
          />
        </Grid>
        {!readOnly && !initialData?.id && (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end">
              <Tooltip title={formatMessage(intl, "prl", "sessionPlanning.removeSession")}>
                <IconButton
                  onClick={() => handleRemoveSession(session.id)}
                  style={{ color: "#d32f2f" }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  const handleBack = () => {
    history.push(`/${PRL_ROUTE_SESSION_PLANNING}`);
  };

  const handleSave = async () => {
    try {
      if (sessions.length === 0) {
        console.warn('No sessions added to save');
        alert('Por favor, adicione pelo menos uma sessão antes de salvar.');
        return;
      }

      // Validate required fields in basic info
      const missingBasicFields = [];
      if (!formData.codigoSessao) missingBasicFields.push('Código da Sessão');
      if (!formData.dataPlanejamento) missingBasicFields.push('Data do Planejamento');
      if (!formData.coordenadorDistrital?.id || formData.coordenadorDistrital.id === "") missingBasicFields.push('Coordenador Distrital');
      if (!formData.tecnicosFormadores || formData.tecnicosFormadores.length === 0) missingBasicFields.push('Técnicos Formadores');
      if (!formData.distrito?.id || formData.distrito.id === "") missingBasicFields.push('Distrito');
      if (!formData.modulo?.id) missingBasicFields.push('Módulo PEP+');

      if (missingBasicFields.length > 0) {
        console.error('Missing required fields in basic info:', missingBasicFields);
        alert(`Por favor, preencha os seguintes campos obrigatórios na seção de informações básicas:\n• ${missingBasicFields.join('\n• ')}`);
        return;
      }

      // Validate each session
      for (let i = 0; i < sessions.length; i++) {
        const session = sessions[i];
        const missingSessionFields = [];

        if (!session.diaSemana) missingSessionFields.push('Dia da semana');
        if (!session.dataSessao) missingSessionFields.push('Data da sessão');
        if (!session.zona || session.zona.trim() === '') missingSessionFields.push('Zona');
        if (!session.horaSessao) missingSessionFields.push('Hora da sessão');
        if (session.numeroFamilias <= 0 || !session.numeroFamilias) missingSessionFields.push('Número de famílias');
        if (!session.grupoFamilia?.id || session.grupoFamilia.id === "") missingSessionFields.push('Grupo de família');
        if (session.tempoDeslocamento <= 0 || !session.tempoDeslocamento) missingSessionFields.push('Tempo de deslocamento');
        if (!session.feedbackDocumentacao || session.feedbackDocumentacao.trim() === '') missingSessionFields.push('Feedback da documentação');

        if (missingSessionFields.length > 0) {
          console.error(`Session ${i + 1} has missing required fields:`, missingSessionFields);
          alert(`Sessão ${i + 1} - Preencha os seguintes campos obrigatórios:\n• ${missingSessionFields.join('\n• ')}`);
          return;
        }
      }

      const isEditMode = !!initialData?.id;

      let input;
      let mutation;

      // Helper function to extract numeric ID from Relay Global ID
      const extractNumericId = (relayId) => {
        if (!relayId) return null;
        // If it's already a small number (valid DB ID), return it
        if (!isNaN(relayId) && parseInt(relayId) < 2147483647) return parseInt(relayId);
        // Try to decode base64 Relay ID
        try {
          const decoded = atob(relayId);
          const match = decoded.match(/:([0-9]+)$/);
          return match ? parseInt(match[1]) : null;
        } catch {
          return null;
        }
      };

      // Helper to extract UUID from Relay Global ID
      const extractUuid = (relayId) => {
        if (!relayId) return null;
        try {
          // If it's already a UUID (contains dashes and no colon), return it
          if (relayId.includes('-') && !relayId.includes(':')) return relayId;
          const decoded = atob(relayId);
          const parts = decoded.split(':');
          return parts.length > 1 ? parts[1] : relayId;
        } catch {
          return relayId;
        }
      };

      // Helper to check if a session ID is a valid database ID (not a Date.now() timestamp)
      const isValidDbId = (id) => {
        const numericId = extractNumericId(id);
        // Date.now() generates timestamps > 1600000000000, DB IDs are much smaller
        return numericId !== null && numericId < 2147483647;
      };

      if (isEditMode) {
        // Update existing sessions only
        const coordenadorId = formData.coordenadorDistrital.id;
        const tecnicosIds = (formData.tecnicosFormadores || []).map(t => extractUuid(t.id));
        const distritoId = formData.distrito.id;

        const sessionsToUpdate = sessions.map(session => ({
          id: extractNumericId(session.id),
          codigoSessao: formData.codigoSessao,
          dataPlanejamento: formData.dataPlanejamento,
          moduloId: extractNumericId(formData.modulo?.id),
          coordenadorDistritalId: coordenadorId,
          tecnicosFormadoresIds: tecnicosIds,
          distritoId: distritoId,
          mesModuloAnterior: formData.mesModuloAnterior,
          diaSemana: session.diaSemana,
          dataSessao: session.dataSessao,
          horaSessao: session.horaSessao,
          zona: session.zona,
          numeroFamilias: parseInt(session.numeroFamilias),
          grupoFamiliaId: extractNumericId(session.grupoFamilia?.id),
          tempoDeslocamento: parseInt(session.tempoDeslocamento),
          feedbackDocumentacao: session.feedbackDocumentacao,
          temSupervisao: session.temSupervisao,
          observacoes: formData.observacoes,
          status: formData.status,
        }));

        console.log('Updating sessions:', sessionsToUpdate);

        input = { sessions: sessionsToUpdate };
        mutation = updateMultipleMutation;
      } else {
        // Create mode - create multiple sessions
        const coordenadorId = formData.coordenadorDistrital.id;
        const tecnicosIds = (formData.tecnicosFormadores || []).map(t => extractUuid(t.id));
        const distritoId = formData.distrito.id;

        // Validate that IDs are not empty
        if (!coordenadorId || tecnicosIds.length === 0 || !distritoId) {
          console.error('Invalid IDs:', { coordenadorId, tecnicosIds, distritoId });
          alert('Erro: IDs inválidos. Por favor, selecione novamente os coordenadores, técnicos e distritos.');
          return;
        }

        const sessionsInput = sessions.map(session => ({
          codigoSessao: formData.codigoSessao,
          dataPlanejamento: formData.dataPlanejamento,
          coordenadorDistritalId: coordenadorId,
          tecnicosFormadoresIds: tecnicosIds,
          distritoId: distritoId,
          moduloId: extractNumericId(formData.modulo?.id),
          mesModuloAnterior: formData.mesModuloAnterior,
          diaSemana: session.diaSemana,
          dataSessao: session.dataSessao,
          horaSessao: session.horaSessao,
          zona: session.zona,
          numeroFamilias: parseInt(session.numeroFamilias),
          grupoFamiliaId: extractNumericId(session.grupoFamilia.id),
          tempoDeslocamento: parseInt(session.tempoDeslocamento),
          feedbackDocumentacao: session.feedbackDocumentacao,
          temSupervisao: session.temSupervisao,
          observacoes: formData.observacoes,
          status: formData.status,
        }));

        input = { sessions: sessionsInput };
        mutation = createMultipleMutation;
      }

      console.log('Sending payload:', JSON.stringify({ query: mutation, variables: { input } }, null, 2));

      const response = await fetch(`${baseApiUrl}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          ...apiHeaders(),
        },
        body: JSON.stringify({ query: mutation, variables: { input } }),
      });

      const result = await response.json();

      // Handle response based on mutation type
      if (isEditMode) {
        // updateMultipleSessoesPep returns { clientMutationId, internalId }
        if (result.data?.updateMultipleSessoesPep) {
          handleBack();
        } else if (result.errors) {
          console.error('Error updating sessions:', result.errors);
          const errorMessages = result.errors.map(err => err.message).join('\n\n');
          alert(`Erro ao atualizar as sessões:\n\n${errorMessages}`);
        }
      } else {
        // createMultipleSessoesPep returns { clientMutationId, internalId }
        if (result.data?.createMultipleSessoesPep) {
          handleBack();
        } else if (result.errors) {
          console.error('Error creating sessions:', result.errors);
          const errorMessages = result.errors.map(err => err.message).join('\n\n');
          alert(`Erro ao salvar as sessões:\n\n${errorMessages}`);
        }
      }
    } catch (error) {
      console.error('Error in handleSave:', error);
      alert('Erro ao salvar as sessões: ' + error.message);
    }
  };

  return (
    <div className={classes.page}>
      <Helmet title={formatMessage(intl, "prl", "title.sessionPlanning")} />

      <Paper className={classes.paper}>
        <Button onClick={handleBack}>
          <ChevronLeftIcon fontSize="small" />
          <Typography className={classes.headerTitle}>
            {formatMessage(intl, "prl", "tool")} 01 - {formatMessage(intl, "prl", "title.sessionPlanning")}
          </Typography>
        </Button>
      </Paper>

      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {formatMessage(intl, "prl", "sessionPlanning.basicInfo")}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={formatMessage(intl, "prl", "sessionPlanning.introCode")}
              placeholder="Ex: AM-MO01-JAN-2026"
              value={formData.codigoSessao}
              onChange={handleCodigoSessaoChange}
              variant="outlined"
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 16 }}
              error={!!codigoSessaoError}
              helperText={codigoSessaoError || "Formato: AA-MOxx-MMM-AAAA (ex: AM-MO01-JAN-2026)"}
              disabled={readOnly}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="date"
              label={formatMessage(intl, "prl", "sessionPlanning.planningDate")}
              value={formData.dataPlanejamento}
              onChange={handleChange("dataPlanejamento")}
              variant="outlined"
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              disabled={readOnly}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label={formatMessage(intl, "prl", "sessionPlanning.selectDistrict")}
              value={formData.distrito?.id || ""}
              onChange={handleSelectChange("distrito")}
              variant="outlined"
              size="small"
              required
              disabled={readOnly}
            >
              {districts.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label={formatMessage(intl, "prl", "sessionPlanning.selectCoordinator")}
              value={formData.coordenadorDistrital?.id || ""}
              onChange={handleSelectChange("coordenadorDistrital")}
              variant="outlined"
              size="small"
              required
              disabled={true}
            >
              {coordinators.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined" size="small" required>
              <InputLabel shrink={!!formData.tecnicosFormadores?.length}>
                {formatMessage(intl, "prl", "sessionPlanning.selectTrainer")}
              </InputLabel>
              <Select
                multiple
                value={(formData.tecnicosFormadores || []).map(t => t.id)}
                onChange={(event) => {
                  const ids = event.target.value;
                  const selectedItems = ids.map(id => {
                    const found = socialTechnicians.find(opt => opt.value === id);
                    return { id, lastName: found ? found.label : "" };
                  });
                  setFormData(prev => ({ ...prev, tecnicosFormadores: selectedItems }));
                }}
                label={formatMessage(intl, "prl", "sessionPlanning.selectTrainer")}
                renderValue={(selected) => (
                  <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {selected.map((id) => {
                      const found = socialTechnicians.find(opt => opt.value === id);
                      const tecnicoNaFormData = (formData.tecnicosFormadores || []).find(t => t.id === id);
                      const label = found ? found.label : (tecnicoNaFormData?.lastName || tecnicoNaFormData?.username || id);
                      return <Chip key={id} size="small" label={label} />;
                    })}
                  </Box>
                )}
                disabled={readOnly}
                notched={!!formData.tecnicosFormadores?.length}
              >
                {socialTechnicians.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label={formatMessage(intl, "prl", "sessionPlanning.moduleName")}
              value={formData.modulo?.id || ""}
              onChange={(e) => setFormData(prev => ({ ...prev, modulo: { id: e.target.value } }))}
              variant="outlined"
              size="small"
              required
              disabled={readOnly}
            >
              {modules.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label={formatMessage(intl, "prl", "sessionPlanning.prevModuleMonth")}
              value={formData.mesModuloAnterior}
              onChange={handleChange("mesModuloAnterior")}
              variant="outlined"
              size="small"
              required
              disabled={readOnly}
            >
              {MONTHS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={formatMessage(intl, "prl", "sessionPlanning.observations")}
              placeholder="Observações adicionais..."
              value={formData.observacoes}
              onChange={handleChange("observacoes")}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              disabled={readOnly}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.paper}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {formatMessage(intl, "prl", "sessionPlanning.planningSessions")}
          </Typography>
          {!readOnly && !initialData?.id && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddSession}
              size="small"
            >
              {formatMessage(intl, "prl", "sessionPlanning.addSession")}
            </Button>
          )}
        </Box>

        {!readOnly && !initialData?.id &&
          <Grid container spacing={3} style={{ marginBottom: "24px", paddingBottom: "16px", borderBottom: "1px solid #e0e0e0" }}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label={formatMessage(intl, "prl", "sessionPlanning.dayOfWeek")}
                value={sessionData.diaSemana}
                onChange={handleSessionChange("diaSemana")}
                variant="outlined"
                size="small"
                required
                disabled={readOnly}
              >
                {DAYS_OF_WEEK.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                label={formatMessage(intl, "prl", "sessionPlanning.plannedDate")}
                value={sessionData.dataSessao}
                onChange={handleSessionChange("dataSessao")}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                required
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={formatMessage(intl, "prl", "sessionPlanning.zone")}
                placeholder="Ex: A, B, C, D"
                value={sessionData.zona}
                onChange={handleSessionChange("zona")}
                variant="outlined"
                size="small"
                required
                InputLabelProps={{ shrink: true }}
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label={formatMessage(intl, "prl", "sessionPlanning.selectFamilyGroup")}
                value={sessionData.grupoFamilia?.id || ""}
                onChange={handleSessionSelectChange("grupoFamilia")}
                variant="outlined"
                size="small"
                required
                disabled={readOnly}
              >
                {familyGroups.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label={formatMessage(intl, "prl", "sessionPlanning.numFamilies")}
                value={sessionData.numeroFamilias}
                onChange={handleSessionChange("numeroFamilias")}
                variant="outlined"
                size="small"
                required
                disabled={true}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label={formatMessage(intl, "prl", "sessionPlanning.travelTime")}
                value={sessionData.tempoDeslocamento}
                onChange={handleSessionChange("tempoDeslocamento")}
                variant="outlined"
                size="small"
                required
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label={formatMessage(intl, "prl", "sessionPlanning.sessionTime")}
                value={sessionData.horaSessao}
                onChange={handleSessionChange("horaSessao")}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                required
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset" className={classes.formControl} disabled={readOnly}>
                <FormLabel component="legend">{formatMessage(intl, "prl", "sessionPlanning.isSupervised")}</FormLabel>
                <RadioGroup
                  row
                  value={sessionData.temSupervisao ? "Sim" : "Não"}
                  onChange={(event) => setSessionData((prev) => ({ ...prev, temSupervisao: event.target.value === "Sim" }))}
                >
                  <FormControlLabel value="Sim" control={<Radio color="primary" />} label="Sim" />
                  <FormControlLabel value="Não" control={<Radio color="primary" />} label="Não" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={formatMessage(intl, "prl", "sessionPlanning.feedback")}
                placeholder="Feedback da documentação..."
                value={sessionData.feedbackDocumentacao}
                onChange={handleSessionChange("feedbackDocumentacao")}
                variant="outlined"
                size="small"
                required
                InputLabelProps={{ shrink: true }}
                disabled={readOnly}
              />
            </Grid>
          </Grid>
        }

        {sessions.map((session, index) => renderSessionForm(session, index))}
      </Paper>

      <Box className={classes.buttonContainer}>
        {readOnly && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => history.push(`/${PRL_ROUTE_SESSION_PLANNING_FORM}`, { data: initialData, readOnly: false })}
          >
            {formatMessage(intl, "prl", "button.edit")}
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={handleBack}
        >
          {readOnly ? formatMessage(intl, "prl", "button.back") : formatMessage(intl, "prl", "button.cancel")}
        </Button>
        {!readOnly && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            {initialData?.id ? formatMessage(intl, "prl", "button.update") : formatMessage(intl, "prl", "sessionPlanning.savePlanning")}
          </Button>
        )}
      </Box>
    </div>
  );
}

export default withModulesManager(injectIntl(withTheme(withStyles(styles)(SessionPlanningEditPage))));
